/**
 * A conferência no portal da plataforma na mesa (E172.F7.T03, GUS-230 B1.3, GUS-216, ADR-0124 §1).
 *
 * Espelho de `apps/api/types/conferencia_no_portal.py`: cada interface tem exatamente os campos do
 * tipo do servidor, e cada união é o `Literal` dele (`test_e172_f7_contrato_web.py` prende os dois).
 * No verificador da lista o sistema consulta sozinho e registra com `origem: sistema` (E174.F1,
 * ADR-0125); nos demais portais quem consulta é a pessoa, e a tela registra o que a página
 * informou, com o print opcional.
 */
import axios, { type AxiosInstance } from 'axios';

export type SituacaoDoAlvo = 'com_endereco' | 'so_codigo' | 'sem_registro';
export type OrigemDoEndereco = 'comprovante' | 'codigo_lido';
export type HostDosCodigosLidos = 'confere' | 'diverge' | 'sem_codigo_lido';
export type EstadoDaConferencia =
  | 'consistente'
  | 'divergente'
  | 'indisponivel'
  | 'nao_executada'
  | 'nao_aplicavel';
export type ResultadoDoCampo = 'confere' | 'diverge' | 'nao_informado';
export type TipoDoPrint = 'image/png' | 'image/jpeg' | 'application/pdf';
export type OrigemDaConferencia = 'assistente' | 'sistema';
export type SituacaoDaResposta = 'respondeu' | 'nao_localizado' | 'falhou';

export interface DocumentoAbrangidoData {
  documento_id: string;
  nome: string;
  sha256: string;
}

export interface AlvoDaConferenciaData {
  situacao: SituacaoDoAlvo;
  plataforma: string | null;
  url: string | null;
  origem_do_endereco: OrigemDoEndereco | null;
  host: string | null;
  https: boolean;
  codigo: string | null;
  codigo_no_endereco: boolean;
  host_dos_codigos_lidos: HostDosCodigosLidos;
  envelope_id: string | null;
  documentos: DocumentoAbrangidoData[];
}

export interface CampoConfrontadoData {
  rotulo: string;
  no_portal: string | null;
  no_material: string | null;
  resultado: ResultadoDoCampo;
}

export interface PrintDaPaginaData {
  sha256: string;
  tipo: TipoDoPrint;
  tamanho: number;
}

/** E172.F11: o que a página do verificador devolveu, fora o dado pessoal. */
export interface RegistroDevolvidoData {
  nome_do_documento: string | null;
  codigo: string | null;
  criado_em: string | null;
  assinado_em: string[];
}

export interface ConsultaAutomaticaData {
  situacao: SituacaoDaResposta;
  registro_valido: boolean | null;
  resposta_sha256: string | null;
  resposta_tamanho: number | null;
  devolvido: RegistroDevolvidoData | null;
}

export interface ConferenciaNoPortalData {
  versao: number;
  estado: EstadoDaConferencia;
  url: string | null;
  codigo: string | null;
  consultado_em: string | null;
  campos: CampoConfrontadoData[];
  motivo: string | null;
  endereco_do_material: boolean;
  alvo: AlvoDaConferenciaData;
  print_da_pagina: PrintDaPaginaData | null;
  operador_id: string | null;
  origem: OrigemDaConferencia;
  consulta_automatica: ConsultaAutomaticaData | null;
}

export interface ConferenciaRegistradaData {
  id: string;
  registrada_em: string;
  conferencia: ConferenciaNoPortalData;
}

export interface PainelDaConferenciaData {
  alvo: AlvoDaConferenciaData;
  registradas: ConferenciaRegistradaData[];
}

export interface ConferenciaNoPortalInData {
  estado: EstadoDaConferencia;
  url?: string | null;
  codigo?: string | null;
  consultado_em?: string | null;
  campos?: CampoConfrontadoData[];
  motivo?: string | null;
}

/** Por que a leitura ou o registro falhou, pela resposta do servidor. */
export type MotivoDaFalhaDoPortal =
  | 'indisponivel'
  | 'sem_permissao'
  | 'nao_encontrado'
  | 'invalida'
  | 'nao_aplicavel_com_registro'
  | 'instante_no_futuro'
  | 'print_sem_consulta'
  | 'print_de_tipo_nao_aceito'
  | 'print_acima_do_teto';

export class ConferenciaNoPortalError extends Error {
  constructor(public readonly motivo: MotivoDaFalhaDoPortal) {
    super(motivo);
    this.name = 'ConferenciaNoPortalError';
  }
}

const _POR_DETALHE: Record<string, MotivoDaFalhaDoPortal> = {
  CASE_NOT_FOUND: 'nao_encontrado',
  EQUIPE_REQUIRED: 'sem_permissao',
  CONFERENCIA_INVALIDA: 'invalida',
  CONFERENCIA_NAO_APLICAVEL_COM_REGISTRO: 'nao_aplicavel_com_registro',
  CONFERENCIA_INSTANTE_NO_FUTURO: 'instante_no_futuro',
  CONFERENCIA_PRINT_SEM_CONSULTA: 'print_sem_consulta',
  CONFERENCIA_PRINT_TIPO_NAO_ACEITO: 'print_de_tipo_nao_aceito',
  CONFERENCIA_PRINT_ACIMA_DO_TETO: 'print_acima_do_teto',
};

function motivoDoErro(err: unknown): MotivoDaFalhaDoPortal {
  if (!axios.isAxiosError(err) || err.response === undefined) return 'indisponivel';
  const detalhe: unknown = (err.response.data as { detail?: unknown } | undefined)?.detail;
  const pelaRecusa = typeof detalhe === 'string' ? _POR_DETALHE[detalhe] : undefined;
  if (pelaRecusa) return pelaRecusa;
  if (err.response.status === 403) return 'sem_permissao';
  if (err.response.status === 404) return 'nao_encontrado';
  return err.response.status === 422 ? 'invalida' : 'indisponivel';
}

function rota(caseId: string): string {
  return `/casos/${caseId}/conferencia-no-portal`;
}

export async function lerConferenciaNoPortal(
  client: AxiosInstance,
  caseId: string,
): Promise<PainelDaConferenciaData> {
  try {
    const { data } = await client.get<PainelDaConferenciaData>(rota(caseId));
    return data;
  } catch (err) {
    throw new ConferenciaNoPortalError(motivoDoErro(err));
  }
}

export async function registrarConferenciaNoPortal(
  client: AxiosInstance,
  caseId: string,
  entrada: ConferenciaNoPortalInData,
  printDaPagina: File | null,
): Promise<PainelDaConferenciaData> {
  const form = new FormData();
  form.append('dados', JSON.stringify(entrada));
  if (printDaPagina) form.append('print_da_pagina', printDaPagina);
  try {
    const { data } = await client.post<PainelDaConferenciaData>(rota(caseId), form);
    return data;
  } catch (err) {
    throw new ConferenciaNoPortalError(motivoDoErro(err));
  }
}

/** O caminho do print no servidor, para abrir pelo cliente autenticado. */
export function caminhoDoPrint(caseId: string, consultaId: string): string {
  return `${rota(caseId)}/${consultaId}/print`;
}

/** Os bytes do print, pelo cliente autenticado (o link simples não leva a credencial). */
export async function lerPrint(
  client: AxiosInstance,
  caseId: string,
  consultaId: string,
): Promise<Blob> {
  try {
    const { data } = await client.get<Blob>(caminhoDoPrint(caseId, consultaId), {
      responseType: 'blob',
    });
    return data;
  } catch (err) {
    throw new ConferenciaNoPortalError(motivoDoErro(err));
  }
}

/** O valor de um `<input type="datetime-local">` com o fuso deste navegador, em ISO 8601. */
export function instanteComFuso(local: string, deslocamentoEmMinutos: number): string {
  const sinal = deslocamentoEmMinutos > 0 ? '-' : '+';
  const total = Math.abs(deslocamentoEmMinutos);
  const horas = String(Math.floor(total / 60)).padStart(2, '0');
  const minutos = String(total % 60).padStart(2, '0');
  const comSegundos = local.length === 16 ? `${local}:00` : local;
  return `${comSegundos}${sinal}${horas}:${minutos}`;
}

/** O fuso em que o exame imprime todo instante (`FUSO_PRESUMIDO` do servidor): UTC-03:00, fixo. */
const FUSO_DO_EXAME_EM_MINUTOS = -180;

const _ISO_COM_FUSO = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:\d{2})$/;

const _dois = (n: number): string => String(n).padStart(2, '0');

/**
 * O instante no fuso e no formato do exame: "26/10/2026 às 10:40 (UTC-03:00)"; com `segundos`,
 * "26/10/2026 às 10:40:15 (UTC-03:00)", como o exame imprime a consulta ao portal.
 *
 * E184.F4 (GUS-145 item 7, mesa do GT-2026-0243): a mesa imprimia o instante com o fuso gravado —
 * a consulta automática grava em UTC e saía "18:42 (UTC+00:00)", ao lado do exame em UTC-03:00.
 */
export function instanteDoExame(iso: string, { segundos = false } = {}): string {
  if (!_ISO_COM_FUSO.test(iso)) return iso;
  const d = new Date(Date.parse(iso) + FUSO_DO_EXAME_EM_MINUTOS * 60_000);
  const hora = `${_dois(d.getUTCHours())}:${_dois(d.getUTCMinutes())}`;
  const comSegundos = segundos ? `${hora}:${_dois(d.getUTCSeconds())}` : hora;
  const data = `${_dois(d.getUTCDate())}/${_dois(d.getUTCMonth() + 1)}/${d.getUTCFullYear()}`;
  return `${data} às ${comSegundos} (UTC-03:00)`;
}

/** O hash abreviado para a tela; o inteiro vai no título do elemento. */
export function hashAbreviado(sha256: string): string {
  return `${sha256.slice(0, 12)}…${sha256.slice(-8)}`;
}
